import * as React from "react";
import { useLocation } from "wouter";
import { RefreshCcw, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";

/**
 * ReorganizeEntry — the bridge that makes "reorganize from a space" and
 * "reorganize from a goal" the SAME mechanics (flow restructure spec §7.3).
 *
 * A reorganize goal's detail view (ReorganizeGoalView) composes the shared
 * PlacementEngine (diagnose → suggest → trial → keep/put-back). This entry
 * point, rendered on a space's detail page, starts a `reorganize` goal scoped to
 * that space and routes into that same engine — so the trial-based placement
 * mechanics are reached from two natural entry points (a space, or the goals
 * list) instead of being reimplemented per surface.
 *
 * (This is complementary to the AI container-fit quick tool; it gives the space
 * page access to the full, trial-guarded placement engine.)
 */
export function ReorganizeEntry({
  spaceId,
  spaceName,
  variant = "button",
}: {
  spaceId: string;
  spaceName: string;
  /** "button" = inline text button; "card" = a full-width promoted card. */
  variant?: "button" | "card";
}) {
  const [, navigate] = useLocation();
  const toast = useToast();
  const [creating, setCreating] = React.useState(false);

  const start = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await apiFetch<{ id: string }>("/api/organizing-goals", {
        method: "POST",
        body: JSON.stringify({
          title: `Reorganize ${spaceName}`,
          description: null,
          activityLocationId: spaceId,
          type: "reorganize",
        }),
      });
      toast.show("Reorganize started — try a placement and keep or put back.", "success");
      navigate(`/organize/goals/${created.id}`);
    } catch (err: any) {
      toast.show(err.message || "Failed to start reorganize", "error");
      setCreating(false);
    }
  };

  if (variant === "card") {
    return (
      <button
        onClick={start}
        disabled={creating}
        className="w-full flex items-center gap-3 p-4 rounded-2xl border border-neutral-border/30 bg-white hover:border-primary/30 transition-colors text-left disabled:opacity-50"
      >
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          {creating ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <RefreshCcw className="w-5 h-5 text-primary" />}
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-900">Reorganize this space</div>
          <div className="text-xs text-gray-400">Try a new arrangement for two weeks, then keep or put back.</div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={start}
      disabled={creating}
      className="text-xs text-primary font-medium hover:text-primary/80 py-2 flex items-center gap-1 disabled:opacity-50"
    >
      {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
      Reorganize as goal
    </button>
  );
}
